import React, { useState, useEffect } from 'react';
import tokenPriceStore from '../../services/tokenPriceStore';

const PriceDashboard = ({ 
  contract, 
  setError,
  setSuccess,
  setLoading,
  loading 
}) => {
  // Stats and monitored tokens
  const [stats, setStats] = useState(tokenPriceStore.getStats());
  const [monitoredTokens, setMonitoredTokens] = useState([]);
  const [prices, setPrices] = useState({});
  const [lastUpdated, setLastUpdated] = useState({});
  const [refreshToggle, setRefreshToggle] = useState(false);
  
  // Load data from the price store
  useEffect(() => {
    // Get the list of monitored tokens
    const tokens = Array.from(tokenPriceStore.monitoredTokens);
    setMonitoredTokens(tokens);
    
    // Get current prices and last updated times
    const currentPrices = {};
    const updateTimes = {};
    
    tokens.forEach(token => {
      currentPrices[token] = tokenPriceStore.getPrice(token);
      updateTimes[token] = tokenPriceStore.getLastUpdated(token);
    });
    
    setPrices(currentPrices);
    setLastUpdated(updateTimes);
    
    // Listen for updates from the price store
    const handleUpdate = (data) => {
      setStats(tokenPriceStore.getStats());
      setPrices(data.prices);
      setLastUpdated(data.lastUpdated);
      setMonitoredTokens(Array.from(tokenPriceStore.monitoredTokens));
    };
    
    tokenPriceStore.addListener(handleUpdate);
    
    return () => {
      tokenPriceStore.removeListener(handleUpdate);
    };
  }, [refreshToggle]);
  
  // Format time since last update
  const formatTimeSince = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 60) return `${diffSec} sec ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    return `${Math.floor(diffSec / 3600)} hours ago`;
  };
  
  // Format a timestamp as a time string
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString();
  };
  
  // Force refresh all prices
  const refreshAllPrices = async () => {
    try {
      setLoading(true);
      await tokenPriceStore.updateAllPrices();
      setSuccess('Successfully refreshed all token prices');
      setRefreshToggle(!refreshToggle); // Trigger re-render
    } catch (error) {
      setError(`Failed to refresh prices: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Force refresh a specific token
  const refreshToken = async (tokenAddress) => {
    try {
      setLoading(true);
      await tokenPriceStore.forceUpdate([tokenAddress]);
      setSuccess(`Successfully refreshed price for ${tokenAddress}`);
      setRefreshToggle(!refreshToggle); // Trigger re-render
    } catch (error) {
      setError(`Failed to refresh price: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Remove a token from monitoring
  const removeToken = (tokenAddress) => {
    try {
      tokenPriceStore.removeTokensFromMonitor([tokenAddress]);
      setSuccess(`Removed ${tokenAddress} from monitoring`);
      setRefreshToggle(!refreshToggle); // Trigger re-render
    } catch (error) {
      setError(`Failed to remove token: ${error.message}`);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Token Price Dashboard</h2>
        <p className="text-gray-600 mb-4">
          Monitor token price updates and status of the background price service.
        </p>
        
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="text-lg font-medium text-blue-700">Monitored Tokens</h3>
            <p className="text-3xl font-bold text-blue-800">{stats.monitoredTokenCount || 0}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-md">
            <h3 className="text-lg font-medium text-green-700">Successful Updates</h3>
            <p className="text-3xl font-bold text-green-800">{stats.successfulUpdates || 0}</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-md">
            <h3 className="text-lg font-medium text-purple-700">Last Updated</h3>
            <p className="text-lg font-bold text-purple-800">
              {stats.lastUpdateTime ? formatTime(stats.lastUpdateTime) : 'Never'}
            </p>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6">
          <button
            onClick={refreshAllPrices}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Refresh All Prices
          </button>
          
          <button
            onClick={() => {
              const exportData = {
                prices: tokenPriceStore.prices,
                lastUpdated: tokenPriceStore.lastUpdated,
                monitoredTokens: Array.from(tokenPriceStore.monitoredTokens),
                stats: tokenPriceStore.getStats()
              };
              
              const dataStr = JSON.stringify(exportData, null, 2);
              const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
              
              const exportFileDefaultName = 'price-store-data.json';
              
              const linkElement = document.createElement('a');
              linkElement.setAttribute('href', dataUri);
              linkElement.setAttribute('download', exportFileDefaultName);
              linkElement.click();
              
              setSuccess('Successfully exported price data');
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Export Data
          </button>
        </div>
        
        {/* Token Table */}
        <div className="overflow-x-auto">
          <h3 className="text-lg font-medium mb-2">Monitored Tokens</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Token Address
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {monitoredTokens.map(token => {
                const price = prices[token];
                const updatedTime = lastUpdated[token];
                const isStale = tokenPriceStore.isPriceStale(token);
                
                return (
                  <tr key={token}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {token.substring(0, 6)}...{token.substring(token.length - 4)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${price ? price.toFixed(2) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {updatedTime ? formatTimeSince(updatedTime) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isStale ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {isStale ? 'Stale' : 'Fresh'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => refreshToken(token)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Refresh
                      </button>
                      <button
                        onClick={() => removeToken(token)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
              {monitoredTokens.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No tokens currently being monitored
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Add Token Form */}
        <div className="mt-8">
          <h3 className="text-lg font-medium mb-2">Add Token to Monitor</h3>
          <div className="flex space-x-2">
            <input
              type="text"
              id="new-token"
              placeholder="Token address (0x...)"
              className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <button
              onClick={() => {
                const input = document.getElementById('new-token');
                const address = input.value.trim();
                
                if (!address) {
                  setError('Please enter a token address');
                  return;
                }
                
                if (!address.startsWith('0x') || address.length !== 42) {
                  setError('Invalid token address format');
                  return;
                }
                
                try {
                  tokenPriceStore.addTokensToMonitor([address]);
                  setSuccess(`Added ${address} to monitoring`);
                  setRefreshToggle(!refreshToggle); // Trigger re-render
                  input.value = '';
                } catch (error) {
                  setError(`Failed to add token: ${error.message}`);
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Add Token
            </button>
          </div>
        </div>
        
        {/* Service Controls */}
        <div className="mt-8">
          <h3 className="text-lg font-medium mb-2">Service Controls</h3>
          <div className="flex space-x-4">
            <button
              onClick={() => {
                tokenPriceStore.startBackgroundUpdates();
                setSuccess('Background price service started');
                setRefreshToggle(!refreshToggle);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Start Service
            </button>
            
            <button
              onClick={() => {
                tokenPriceStore.stopBackgroundUpdates();
                setSuccess('Background price service stopped');
                setRefreshToggle(!refreshToggle);
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Stop Service
            </button>
            
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  const success = await tokenPriceStore.resetToDefaults();
                  if (success) {
                    setSuccess('Reset to default tokens and mappings successfully');
                  } else {
                        setError('Failed to reset to defaults');
                  }
                    setRefreshToggle(!refreshToggle);
                } catch (error) {
                  setError(`Failed to reset to defaults: ${error.message}`);
                } finally {
                  setLoading(false);
                }
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
            >
              Clear Cache
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceDashboard;